// src/components/LogsPanel.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Fab,
  Zoom
} from '@mui/material';
import {
  Clear,
  Download,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  Terminal,
  Error,
  Warning,
  CheckCircle,
  Info,
  BugReport
} from '@mui/icons-material';

const LogsPanel = ({
  logs = [],
  autoScroll,
  setAutoScroll,
  logFilter,
  setLogFilter,
  logExpanded,
  setLogExpanded,
  logSearch,
  setLogSearch,
  logLevels,
  setLogLevels,
  clearLogs,
  exportLogs,
  scrollToTop,
  scrollToBottom,
  logAreaRef
}) => {
  // Фильтрация логов по поиску и уровням
  const filteredLogs = (logs || []).filter(log => {
    if (!log) return false;
    
    const matchesSearch = log.message?.toLowerCase().includes(logSearch.toLowerCase()) ||
                         (log.data && JSON.stringify(log.data).toLowerCase().includes(logSearch.toLowerCase()));
    const matchesLevel = logLevels[log.type];
    return matchesSearch && matchesLevel;
  });

  // Автоскролл при новых логах
  useEffect(() => {
    if (autoScroll && logAreaRef?.current) {
      logAreaRef.current.scrollTop = logAreaRef.current.scrollHeight;
    }
  }, [logs, autoScroll, logAreaRef]);

  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0,
      height: '100%' // Занимаем всю доступную высоту
    }}>
      {/* Шапка с контролами */}
      <Box sx={{ 
        p: 2, 
        pb: 1, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0 // Фиксированная высота шапки
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Terminal />
          <Typography variant="h6">
            Логи ({filteredLogs.length}/{(logs || []).length})
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setLogExpanded(!logExpanded)}
            title={logExpanded ? 'Свернуть' : 'Развернуть'}
          >
            {logExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Поиск по логам */}
          <TextField
            size="small"
            placeholder="Поиск в логах..."
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            sx={{ width: 150 }}
          />
          
          {/* Фильтры по уровням */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {Object.entries(logLevels).map(([level, enabled]) => (
              <Tooltip key={level} title={`${level} ${enabled ? 'скрыть' : 'показать'}`}>
                <IconButton
                  size="small"
                  onClick={() => setLogLevels(prev => ({ ...prev, [level]: !prev[level] }))}
                  sx={{ 
                    opacity: enabled ? 1 : 0.3,
                    color: 
                      level === 'error' ? 'error.main' :
                      level === 'warning' ? 'warning.main' :
                      level === 'success' ? 'success.main' :
                      level === 'info' ? 'info.main' : 'text.secondary'
                  }}
                >
                  {level === 'error' && <Error fontSize="small" />}
                  {level === 'warning' && <Warning fontSize="small" />}
                  {level === 'success' && <CheckCircle fontSize="small" />}
                  {level === 'info' && <Info fontSize="small" />}
                  {level === 'debug' && <BugReport fontSize="small" />}
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          <FormControl size="small">
            <Select
              value={logFilter}
              onChange={e => setLogFilter(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="success">Успех</MenuItem>
              <MenuItem value="error">Ошибки</MenuItem>
              <MenuItem value="warning">Предупреждения</MenuItem>
              <MenuItem value="info">Инфо</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={autoScroll}
                onChange={e => setAutoScroll(e.target.checked)}
                size="small"
              />
            }
            label="Автоскролл"
          />
          
          <Tooltip title="В начало">
            <IconButton onClick={scrollToTop} size="small">
              <ExpandLess />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="В конец">
            <IconButton onClick={scrollToBottom} size="small">
              <ExpandMoreIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Экспорт логов">
            <IconButton onClick={exportLogs} size="small">
              <Download />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Очистить логи">
            <IconButton onClick={clearLogs} size="small">
              <Clear />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Область логов - адаптивная высота */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', // Скрываем переполнение контейнера
        backgroundColor: '#1e1e1e',
        position: 'relative'
      }}>
        <Box
          ref={logAreaRef}
          sx={{
            p: 2,
            fontFamily: '"Fira Code", "Courier New", monospace',
            fontSize: '13px',
            lineHeight: '1.4',
            height: '100%', // Занимаем всю высоту родителя
            overflow: 'auto', // Включаем прокрутку внутри
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#e0e0e0',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: '#2d2d2d',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#555',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#777',
            },
          }}
        >
          {filteredLogs.length === 0 ? (
            <Typography 
              color="text.secondary" 
              sx={{ 
                textAlign: 'center', 
                py: 4,
                fontStyle: 'italic',
                color: '#666'
              }}
            >
              {(logs || []).length === 0 ? 'Логи отсутствуют' : 'Нет логов, соответствующих фильтрам'}
            </Typography>
          ) : (
            filteredLogs.map((log, index) => (
              <Box
                key={log.id || index}
                sx={{
                  mb: 0.5,
                  padding: '4px 8px',
                  borderRadius: 1,
                  backgroundColor: 
                    log.type === 'error' ? 'rgba(244, 67, 54, 0.1)' :
                    log.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
                    log.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                  borderLeft: `3px solid ${
                    log.type === 'error' ? '#f44336' :
                    log.type === 'warning' ? '#ff9800' :
                    log.type === 'success' ? '#4caf50' : 
                    log.type === 'debug' ? '#9c27b0' : '#2196f3'
                  }`,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ 
                    flexShrink: 0, 
                    fontSize: '12px',
                    color: 
                      log.type === 'error' ? '#f44336' :
                      log.type === 'warning' ? '#ff9800' :
                      log.type === 'success' ? '#4caf50' : 
                      log.type === 'debug' ? '#9c27b0' : '#2196f3'
                  }}>
                    {log.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ 
                      fontWeight: '500',
                      color: '#e0e0e0',
                      fontSize: '13px'
                    }}>
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        {log.timestamp ? `[${new Date(log.timestamp).toLocaleTimeString()}]` : '[--:--:--]'}
                      </span> {log.message}
                    </Box>
                    {log.data && (
                      <Box 
                        sx={{ 
                          mt: 0.5, 
                          ml: 2, 
                          p: 1, 
                          backgroundColor: 'rgba(0, 0, 0, 0.3)', 
                          borderRadius: 1,
                          border: '1px solid #333',
                          fontSize: '12px',
                          overflowX: 'auto'
                        }}
                      >
                        <pre style={{ margin: 0, fontFamily: 'inherit' }}>
                          <code style={{ color: '#8bc34a' }}>
                            {JSON.stringify(log.data, null, 2)}
                          </code>
                        </pre>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Кнопка быстрой прокрутки вниз */}
        {!autoScroll && (
          <Zoom in={true}>
            <Fab
              color="primary"
              size="small"
              onClick={scrollToBottom}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 1000
              }}
            >
              <ExpandMoreIcon />
            </Fab>
          </Zoom>
        )}
      </Box>
    </Box>
  );
};

export default LogsPanel;